import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { cn } from "@/lib/utils";
import { adaptMarketHistory } from "@/lib/marketHistoryData";
import { DateRangePopover } from "@/components/dashboard/DateRangePopover";
import { Maximize2, Minimize2, X, Table as TableIcon, LineChart as LineIcon, RotateCcw, Download } from "lucide-react";

const C = { featuredOffer: "#087a5b", lowestWfs: "#2774e6", lowestSellerFulfilled: "#0396a6", walmartRetailPrice: "#d68b00", sourceCost: "#d74343", estimatedMonthlyUnits: "#7457d9", totalOffers: "#0396a6", sellerCount: "#e06455", wfsOffers: "#4b72d1", walmartRetail: "#d68b00" };
const W = 1000, P = { l: 55, r: 18, t: 16, b: 28 };
const RANGES = ["7D", "30D", "90D", "180D", "1Y", "All"];
const usd = (v) => "$" + Number(v).toFixed(2);
const fmtTs = (d) => d.toLocaleString("en-US", { weekday: "short", month: "short", day: "numeric", year: "numeric", hour: "numeric", minute: "2-digit" });

const ROWS = [
  { id: "price", title: "Price history", h: 220, hx: 325, fmt: usd, series: [["featuredOffer", "Featured Offer"], ["lowestWfs", "Lowest WFS"], ["lowestSellerFulfilled", "Lowest seller-fulfilled"], ["walmartRetailPrice", "Walmart Retail"], ["sourceCost", "Break-even / source"]] },
  { id: "demand", title: "Demand history", h: 140, hx: 225, estimated: true, fmt: (v) => Math.round(v).toLocaleString() + " units", series: [["estimatedMonthlyUnits", "Estimated monthly units"]] },
  { id: "competition", title: "Competition history", h: 140, hx: 225, fmt: (v) => Math.round(v), series: [["totalOffers", "Total offers"], ["sellerCount", "Seller count"], ["wfsOffers", "WFS offers"], ["walmartRetail", "Walmart Retail"]] },
];
const valOf = (d, k) => k === "walmartRetail" ? (d.walmartRetailInStock ? 1 : 0) : d[k];

const TIP_GROUPS = [
  { title: "Price", items: [["featuredOffer", "Featured offer", usd], ["lowestWfs", "Lowest WFS", usd], ["lowestSellerFulfilled", "Lowest SF", usd], ["walmartRetailPrice", "Walmart Retail", usd]] },
  { title: "Demand", items: [["estimatedMonthlyUnits", "Estimated units", (v) => v.toLocaleString() + " / mo"]] },
  { title: "Competition", items: [["totalOffers", "Total offers", (v) => v], ["sellerCount", "Sellers", (v) => v], ["wfsOffers", "WFS offers", (v) => v]] },
];

function linePath(vals, x, y, step) { let p = "", on = false; vals.forEach((v, i) => { if (v == null) { on = false; return; } const X = x(i), Y = y(v); if (!on) { p += `M${X},${Y}`; on = true; } else p += step ? `H${X}V${Y}` : `L${X},${Y}`; }); return p; }
function areaPath(vals, x, y, base, gid) { let out = "", seg = []; const flush = () => { if (!seg.length) return; out += `<path fill="url(#${gid})" d="${linePath(seg.map((s) => s.value), (i) => x(seg[i].index), y, true)} L${x(seg[seg.length - 1].index)},${base} L${x(seg[0].index)},${base} Z"/>`; seg = []; }; vals.forEach((value, index) => value == null ? flush() : seg.push({ value, index })); flush(); return out; }

function buildSvg(def, data, H) {
  const gid = "wg-" + def.id;
  const active = def.series.filter(([k]) => data.__vis.has(k));
  const vals = active.flatMap(([k]) => data.map((d) => valOf(d, k)).filter((v) => v != null));
  let min = Math.min(...vals), max = Math.max(...vals);
  if (!isFinite(min)) { min = 0; max = 1; }
  if (def.id === "price") { min = Math.floor(min - 1); max = Math.ceil(max + 1); } else { min = Math.max(0, Math.floor(min * 0.85)); max = Math.ceil(max * 1.12) || 1; }
  const x = (i) => P.l + i / Math.max(1, data.length - 1) * (W - P.l - P.r);
  const y = (v) => P.t + (max - v) / (max - min || 1) * (H - P.t - P.b);
  const short = data.length > 1 && (data[data.length - 1].timestamp - data[0].timestamp) <= 8 * 864e5;
  let s = `<svg width="100%" height="100%" style="display:block" viewBox="0 0 ${W} ${H}" preserveAspectRatio="none" role="img" aria-label="${def.title}"><defs><linearGradient id="${gid}" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stop-color="${C.walmartRetailPrice}" stop-opacity=".26"/><stop offset="65%" stop-color="${C.walmartRetailPrice}" stop-opacity=".08"/><stop offset="100%" stop-color="${C.walmartRetailPrice}" stop-opacity="0"/></linearGradient></defs>`;
  for (let i = 0; i < 5; i++) { const yy = P.t + i * (H - P.t - P.b) / 4, val = max - i * (max - min) / 4; s += `<line class="grid ${i === 4 ? "major" : ""}" x1="${P.l}" y1="${yy}" x2="${W - P.r}" y2="${yy}"/><text class="axis" x="${P.l - 8}" y="${yy + 4}" text-anchor="end">${def.id === "price" ? "$" + val.toFixed(0) : Math.round(val).toLocaleString()}</text>`; }
  for (let i = 0; i < 5; i++) { const xx = P.l + i * (W - P.l - P.r) / 4, idx = Math.round(i * (data.length - 1) / 4); s += `<line class="grid" x1="${xx}" y1="${P.t}" x2="${xx}" y2="${H - P.b}"/><text class="axis" x="${xx}" y="${H - 9}" text-anchor="middle">${data[idx]?.timestamp.toLocaleString("en-US", short ? { month: "short", day: "numeric", hour: "numeric" } : { month: "short", day: "numeric" }) || ""}</text>`; }
  if (def.id === "demand" && data.__vis.has("estimatedMonthlyUnits")) {
    // Only draw the estimate band when there is at least one genuine value.
    // Emitting it for an all-null series produced an invalid path ("  Z").
    const hi = data.map((d) => d.demandHigh), lo = data.map((d) => d.demandLow);
    if (hi.some((v) => v != null) && lo.some((v) => v != null)) {
      s += `<path class="band" fill="${C.estimatedMonthlyUnits}" d="${linePath(hi, x, y)} ${linePath(lo.slice().reverse(), (i) => x(data.length - 1 - i), y).replace("M", "L")} Z"/>`;
    }
  }
  if (def.id === "price" && data.__vis.has("walmartRetailPrice")) s += areaPath(data.map((d) => d.walmartRetailPrice), x, y, H - P.b, gid);
  if (def.id === "competition" && data.__vis.has("walmartRetail")) s += areaPath(data.map((d) => valOf(d, "walmartRetail")), x, y, H - P.b, gid);
  active.forEach(([k]) => {
    const step = def.id !== "demand";
    const vals = data.map((d) => valOf(d, k));
    s += `<path class="line ${step ? "step" : "estimated"}" stroke="${C[k]}" d="${linePath(vals, x, y, step)}"/>`;
    // Stage AM: a path with a single point is just "M…" and renders NOTHING, so
    // the first real observation used to produce an empty chart. Every observed
    // value now gets a visible marker; with one observation that marker is the
    // whole chart, which is exactly what a single check deserves.
    vals.forEach((v, i) => {
      if (v == null) return;
      s += `<circle class="obs" cx="${x(i).toFixed(1)}" cy="${y(v).toFixed(1)}" r="${vals.filter((x2) => x2 != null).length === 1 ? 4 : 2.5}" fill="#fff" stroke="${C[k]}" stroke-width="2"/>`;
    });
  });
  s += `</svg>`;
  return s;
}

const STAT_SERIES = [
  ["catalog_price", "Walmart Retail", usd],
  ["featured_offer", "Featured offer", usd],
  ["lowest_wfs", "Lowest WFS", usd],
  ["lowest_mfn", "Lowest SF", usd],
  ["source_cost", "Break-even / source", usd],
  ["estimated_monthly_units", "Est. units/mo", (v) => Math.round(v).toLocaleString()],
  ["seller_count", "Sellers", (v) => Math.round(v)],
  ["total_offers", "Offers", (v) => Math.round(v)],
];

const fmtDay = (iso) => {
  if (!iso) return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleString("en-US", { month: "short", day: "numeric", year: "numeric" });
};

/**
 * Latest / previous / change / min-max / count for every series that actually
 * has observations. A series with no observation is omitted or explicitly
 * called out — missing data is never rendered as a zero.
 */
function HistoryStatsStrip({ stats, availability }) {
  const rows = STAT_SERIES.map(([key, label, fmt]) => ({ key, label, fmt, s: stats?.series?.[key] }))
    .filter((r) => r.s && r.s.available);
  const inStock = stats?.series?.walmart_retail_in_stock;
  const missing = [];
  if (availability && !availability.competition?.seller_count?.available) missing.push("seller counts");
  if (availability && !availability.demand?.est_units?.available) missing.push("demand estimates");
  if (availability && !availability.price?.lowest_wfs?.available) missing.push("WFS offers");

  return (
    <div data-testid="mh-stats" className="border-b border-edge-subtle bg-surface-1/40">
      <div className="flex items-stretch gap-0 overflow-x-auto ez-scroll">
        <div className="shrink-0 px-4 py-2 border-r border-edge-subtle">
          <div className="text-[10px] uppercase tracking-wide text-ink-muted">Observations</div>
          <div className="text-compact font-semibold tabnum text-ink-primary">{stats?.observation_count ?? 0}</div>
        </div>
        {rows.map(({ key, label, fmt, s }) => (
          <div key={key} className="shrink-0 px-4 py-2 border-r border-edge-subtle" data-testid={`mh-stat-${key}`}>
            <div className="text-[10px] uppercase tracking-wide text-ink-muted truncate max-w-[150px]">{label}</div>
            <div className="flex items-baseline gap-1.5">
              <span className="text-compact font-semibold tabnum text-ink-primary">{fmt(s.latest)}</span>
              {s.change != null && s.change !== 0 && (
                <span className={cn("text-[10px] tabnum font-semibold", s.change > 0 ? "text-emerald-700" : "text-red-600")}>
                  {s.change > 0 ? "▲" : "▼"} {fmt(Math.abs(s.change))}
                  {s.change_pct != null && ` (${s.change_pct > 0 ? "+" : ""}${s.change_pct}%)`}
                </span>
              )}
            </div>
            <div className="text-[10px] text-ink-muted tabnum">
              {fmt(s.minimum)}–{fmt(s.maximum)} · {s.count} obs
              {fmtDay(s.latest_observed_at) && ` · ${fmtDay(s.latest_observed_at)}`}
            </div>
          </div>
        ))}
        {inStock?.available && (
          <div className="shrink-0 px-4 py-2 border-r border-edge-subtle" data-testid="mh-stat-walmart-retail">
            <div className="text-[10px] uppercase tracking-wide text-ink-muted">Walmart Retail</div>
            <div className="text-compact font-semibold text-ink-primary">{inStock.final_state === "in_stock" ? "In stock" : "Absent"}</div>
            <div className="text-[10px] text-ink-muted tabnum">{inStock.count} obs{fmtDay(inStock.latest_observed_at) && ` · ${fmtDay(inStock.latest_observed_at)}`}</div>
          </div>
        )}
        {missing.length > 0 && (
          <div className="shrink-0 px-4 py-2 self-center min-w-0">
            <div className="text-[10px] uppercase tracking-wide text-ink-muted">Not observed</div>
            <div className="text-[10px] text-ink-muted truncate max-w-[280px]" title={missing.join(", ")}>{missing.join(", ")}</div>
          </div>
        )}
      </div>
    </div>
  );
}

export function MarketHistory({ history, records: recordsProp, loading = false, error = null, onRetry }) {
  const src = useMemo(
    () => (recordsProp
      ? {
          records: recordsProp,
          demo: false,
          unavailable: false,
          stats: history?.stats || null,
          seriesAvailability: history?.series_availability || null,
        }
      : adaptMarketHistory(history)),
    [recordsProp, history]
  );
  const records = src.records || [];
  const N = records.length;
  const isUnavailable = !loading && !error && (src.unavailable || N === 0) && history != null;
  const isSingleObservation = !loading && !error && !src.unavailable && N === 1;
  const [domain, setDomain] = useState([0, Math.max(0, N - 1)]);
  const [range, setRange] = useState("All");
  const [customLabel, setCustomLabel] = useState(null);
  const [vis, setVis] = useState(() => new Set(ROWS.flatMap((r) => r.series.map((s) => s[0]))));
  const [expanded, setExpanded] = useState(false);
  const [table, setTable] = useState(false);
  const [active, setActive] = useState(null);
  const [tip, setTip] = useState(null);
  const [drag, setDrag] = useState(null);
  const [, force] = useState(0);
  const rootRef = useRef(null);
  const expandBtnRef = useRef(null);

  const spanDays = useMemo(() => {
    if (N < 2) return 0;
    const a = records[0]?.timestamp?.getTime?.();
    const b = records[N - 1]?.timestamp?.getTime?.();
    if (!Number.isFinite(a) || !Number.isFinite(b) || b < a) return 0;
    return (b - a) / 864e5;
  }, [records, N]);

  // Enable ranges only when actual observation span supports them.
  const rangeEnabled = useMemo(() => ({
    "7D": N >= 2,
    "30D": N >= 2 && spanDays >= 1,
    "90D": N >= 2 && spanDays >= 7,
    "180D": N >= 2 && spanDays >= 30,
    "1Y": N >= 2 && spanDays >= 90,
    All: N >= 2,
  }), [N, spanDays]);

  useEffect(() => { applyRange(N >= 2 ? "All" : "All"); /* eslint-disable-next-line */ }, [N]);
  useEffect(() => {
    const ro = new ResizeObserver(() => force((v) => v + 1));
    if (rootRef.current) ro.observe(rootRef.current);
    return () => ro.disconnect();
  }, [expanded]);
  useEffect(() => {
    document.body.style.overflow = expanded ? "hidden" : "";
    const onKey = (e) => { if (e.key === "Escape" && expanded) { setExpanded(false); expandBtnRef.current?.focus(); } };
    if (expanded) window.addEventListener("keydown", onKey);
    return () => { document.body.style.overflow = ""; window.removeEventListener("keydown", onKey); };
  }, [expanded]);

  const now = records[N - 1]?.timestamp || new Date();
  function applyRange(r) {
    if (N <= 0) { setDomain([0, -1]); setRange(r); return; }
    if (r !== "All" && rangeEnabled[r] === false) return;
    setRange(r); setCustomLabel(null);
    if (r === "All") return setDomain([0, N - 1]);
    const days = { "7D": 7, "30D": 30, "90D": 90, "180D": 180, "1Y": 365 }[r];
    const x = records.findIndex((v) => v.timestamp.getTime() >= now.getTime() - days * 864e5);
    setDomain([Math.max(0, x), N - 1]);
  }
  const applyCustom = ({ start, end, label }) => {
    if (N <= 0) return;
    const s = new Date(start); s.setHours(0, 0, 0, 0);
    const e = new Date(end); e.setHours(23, 59, 59, 999);
    let lo = records.findIndex((r) => r.timestamp >= s);
    if (lo < 0) lo = 0;
    let hi = -1;
    for (let i = records.length - 1; i >= 0; i--) { if (records[i].timestamp <= e) { hi = i; break; } }
    setRange("Custom"); setCustomLabel(label);
    setDomain(hi < lo ? [lo, lo - 1] : [lo, hi]);
  };

  const [a, b] = domain;
  const empty = N > 0 && b < a;
  const subset = useMemo(() => { const s = records.slice(a, b + 1); s.__vis = vis; return s; }, [records, a, b, vis]);
  const activeFrac = active != null && active >= a && active <= b && subset.length ? (P.l + (active - a) / Math.max(1, subset.length - 1) * (W - P.l - P.r)) / W : null;

  const toggle = (rowId, key) => setVis((s) => {
    const row = ROWS.find((r) => r.id === rowId);
    const on = row.series.filter(([k]) => s.has(k)).length;
    if (s.has(key) && on === 1) return s;
    const n = new Set(s); n.has(key) ? n.delete(key) : n.add(key); return n;
  });

  const onMove = (e, hit) => {
    if (!subset.length) return;
    const r = hit.getBoundingClientRect();
    const px = Math.max(0, Math.min(1, (e.clientX - r.left) / r.width));
    const idx = Math.round(px * (subset.length - 1));
    setActive(a + idx);
    setTip({ x: e.clientX, y: e.clientY, rec: records[a + idx] });
    if (drag) setDrag({ ...drag, end: px });
  };
  const onDown = (e, hit) => { const r = hit.getBoundingClientRect(); setDrag({ start: Math.max(0, Math.min(1, (e.clientX - r.left) / r.width)), end: null }); try { hit.setPointerCapture(e.pointerId); } catch {} };
  const onUp = () => {
    if (drag && drag.end != null && Math.abs(drag.end - drag.start) > 0.035) {
      const lo = Math.min(drag.end, drag.start), hi = Math.max(drag.end, drag.start), len = b - a;
      setDomain([Math.round(a + lo * len), Math.round(a + hi * len)]); setRange(""); setCustomLabel(null);
    }
    setDrag(null);
  };
  const resetZoom = () => { setDomain([0, Math.max(0, N - 1)]); setRange("All"); setCustomLabel(null); };

  const exportCsv = () => {
    const cols = ["timestamp", "featuredOffer", "lowestWfs", "lowestSellerFulfilled", "walmartRetailPrice", "estimatedMonthlyUnits", "totalOffers", "sellerCount", "wfsOffers", "walmartRetailInStock", "dataQuality"];
    const csv = [cols.join(","), ...records.slice(a, b + 1).map((d) => cols.map((c) => c === "timestamp" ? d.timestamp.toISOString() : (d[c] == null ? "" : d[c])).join(","))].join("\n");
    const link = document.createElement("a"); link.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" })); link.download = "ezisell-market-history.csv"; link.click(); URL.revokeObjectURL(link.href);
  };

  const toolBtn = "inline-flex items-center gap-1.5 h-8 px-2.5 rounded-ctrl text-compact text-ink-secondary hover:bg-emerald-100 hover:text-emerald-700 ez-press";

  const content = error ? (
    <div className="p-12 text-center" data-testid="mh-error"><div className="text-compact text-red-600">Couldn't load market history.</div>{onRetry && <button onClick={onRetry} className="mt-3 h-8 px-4 rounded-ctrl bg-emerald-600 text-white text-compact">Retry</button>}</div>
  ) : loading || src.loading ? (
    <div className="p-4 space-y-3">{ROWS.map((r) => <div key={r.id} className="ez-shimmer rounded-panel" style={{ height: r.h }} />)}</div>
  ) : isUnavailable ? (
    <div className="p-6 text-center" data-testid="mh-unavailable">
      <div className="text-compact font-medium text-ink-primary">
        {src.emptyObserved ? "No observations yet" : "History not available"}
      </div>
      <p className="text-compact text-ink-secondary mt-1 max-w-md mx-auto">
        {src.message || "Walmart Marketplace search does not provide historical prices or seller observations for this product."}
      </p>
      {src.reason && (
        <p className="text-micro font-mono text-ink-muted mt-2" data-testid="mh-unavailable-reason">{src.reason}</p>
      )}
    </div>
  ) : isSingleObservation ? (
    <div className="p-5" data-testid="mh-single-observation">
      {(() => {
        const rec = records[0];
        const price = rec.walmartRetailPrice ?? rec.featuredOffer ?? rec.lowestWfs ?? null;
        const source = rec.source || rec.provider || (rec.dataQuality === "observed" ? "Walmart catalog" : "Observed");
        return (
          <div className="rounded-ctrl border border-edge-subtle bg-surface-1/50 px-4 py-3 max-w-md">
            <div className="text-micro font-mono uppercase text-ink-muted">Latest observed price</div>
            <div className="mt-1 text-[22px] font-extrabold tabnum text-ink-primary">{price == null ? "—" : usd(price)}</div>
            <div className="mt-2 text-compact text-ink-secondary">{fmtTs(rec.timestamp)}</div>
            <div className="mt-1 text-micro text-ink-muted">Source: {source}</div>
            <p className="mt-3 text-compact text-ink-secondary">
              Price history will appear as more observations are collected.
            </p>
          </div>
        );
      })()}
    </div>
  ) : empty ? (
    <div className="p-14 text-center" data-testid="mh-empty"><div className="text-compact font-medium text-ink-primary">No observations in the selected range</div><p className="text-micro text-ink-muted mt-1">Pick a wider custom range, or reset the zoom.</p><button onClick={resetZoom} className="mt-3 h-8 px-4 rounded-ctrl border border-edge-subtle text-compact hover:bg-surface-2">Reset range</button></div>
  ) : table ? (
    <div className="overflow-auto ez-scroll" style={{ maxHeight: expanded ? "none" : 590 }} data-testid="mh-table-view">
      <table className="w-full text-compact" style={{ minWidth: 1050 }}>
        <thead><tr className="text-micro font-medium text-ink-muted text-right bg-surface-2">
          {["Date & time", "Featured", "Lowest WFS", "Lowest SF", "Walmart", "Est. units", "Demand range", "Offers", "Sellers", "WFS", "Retail", "Quality"].map((h, i) => <th key={h} className={cn("px-3 py-2 border-b border-edge-subtle sticky top-0 bg-surface-2", i === 0 && "text-left sticky left-0 z-10")}>{h}</th>)}
        </tr></thead>
        <tbody>
          {records.slice(a, b + 1).slice().reverse().map((d, i) => (
            <tr key={i} className="text-right hover:bg-surface-2">
              <td className="px-3 py-2 border-b border-edge-subtle text-left sticky left-0 bg-surface-0">{fmtTs(d.timestamp)}</td>
              <td className="px-3 py-2 border-b border-edge-subtle">{d.featuredOffer == null ? "—" : usd(d.featuredOffer)}</td>
              <td className="px-3 py-2 border-b border-edge-subtle">{d.lowestWfs == null ? "—" : usd(d.lowestWfs)}</td>
              <td className="px-3 py-2 border-b border-edge-subtle">{d.lowestSellerFulfilled == null ? "—" : usd(d.lowestSellerFulfilled)}</td>
              <td className="px-3 py-2 border-b border-edge-subtle">{d.walmartRetailPrice == null ? "—" : usd(d.walmartRetailPrice)}</td>
              <td className="px-3 py-2 border-b border-edge-subtle">{d.estimatedMonthlyUnits == null ? "—" : d.estimatedMonthlyUnits.toLocaleString()}</td>
              <td className="px-3 py-2 border-b border-edge-subtle">{d.demandLow == null || d.demandHigh == null ? "—" : `${d.demandLow.toLocaleString()}–${d.demandHigh.toLocaleString()}`}</td>
              <td className="px-3 py-2 border-b border-edge-subtle">{d.totalOffers == null ? "—" : d.totalOffers}</td>
              <td className="px-3 py-2 border-b border-edge-subtle">{d.sellerCount == null ? "—" : d.sellerCount}</td>
              <td className="px-3 py-2 border-b border-edge-subtle">{d.wfsOffers == null ? "—" : d.wfsOffers}</td>
              <td className="px-3 py-2 border-b border-edge-subtle">{d.walmartRetailInStock == null ? "—" : d.walmartRetailInStock ? "In stock" : "Absent"}</td>
              <td className="px-3 py-2 border-b border-edge-subtle capitalize">{d.dataQuality}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : (
    <div>
      {ROWS.map((def) => {
        const H = expanded ? def.hx : def.h;
        const rowVisible = def.series.filter(([k]) => vis.has(k));
        const rowHasData = def.series.some(([k]) => subset.some((d) => valOf(d, k) != null));
        const rowEmptyNote = {
          price: "No price observation yet for this Walmart item.",
          demand: "No demand estimate — IziSell only shows units modelled by a documented method, never a guess from reviews.",
          competition: "No offer or seller-count observation — the Walmart catalog API does not return these per item.",
        }[def.id];
        return (
          <div key={def.id} data-testid={`mh-row-${def.id}`} className={cn("grid grid-cols-[150px_minmax(0,1fr)] sm:grid-cols-[190px_minmax(0,1fr)] border-b border-edge-subtle last:border-0", expanded && "sm:grid-cols-[230px_minmax(0,1fr)]")}>
            <div className="border-r border-edge-subtle bg-surface-1/60 p-3">
              <div className="text-compact font-semibold text-ink-primary mb-2">{def.title}</div>
              <div className="space-y-1.5">
                {def.series.map(([k, label]) => {
                  const last = [...subset].reverse().find((d) => valOf(d, k) != null);
                  const on = vis.has(k);
                  return (
                    <button key={k} data-testid={`mh-series-${k}`} onClick={() => toggle(def.id, k)} className={cn("w-full grid grid-cols-[14px_1fr_auto] items-center gap-2 text-left text-micro", on ? "text-ink-secondary" : "opacity-40")}>
                      <span className="h-[3px] rounded" style={{ background: C[k] }} />
                      <span className="truncate">{label}</span>
                      <span className="tabnum font-semibold text-ink-primary hidden sm:block">{!last ? "—" : def.fmt(valOf(last, k))}</span>
                    </button>
                  );
                })}
              </div>
            </div>
            <div className="relative bg-surface-0 mh-plot" style={{ minHeight: H }}>
              <div className="absolute inset-0 [&_.grid]:stroke-[#e9ecef] [&_.grid.major]:stroke-[#dfe3e7] [&_.axis]:fill-[#7a8491] [&_.axis]:text-[10px] [&_.line]:fill-none [&_.line]:stroke-2 [&_.line.estimated]:[stroke-dasharray:5_4] [&_.band]:opacity-[.11]"
                dangerouslySetInnerHTML={{ __html: buildSvg(def, subset, H) }} />
              {activeFrac != null && <div className="absolute top-0 bottom-0 border-l border-dashed border-ink-secondary/60 pointer-events-none" style={{ left: `${activeFrac * 100}%` }} />}
              {drag && drag.end != null && <div className="absolute top-0 bottom-0 bg-signal-blue/10 border-x border-signal-blue pointer-events-none" style={{ left: `${(P.l / W + Math.min(drag.start, drag.end) * ((W - P.l - P.r) / W)) * 100}%`, width: `${Math.abs(drag.end - drag.start) * ((W - P.l - P.r) / W) * 100}%` }} />}
              <div className="absolute cursor-crosshair" style={{ left: `${P.l / W * 100}%`, right: `${P.r / W * 100}%`, top: `${P.t / H * 100}%`, bottom: `${P.b / H * 100}%` }}
                onPointerMove={(e) => onMove(e, e.currentTarget)} onPointerDown={(e) => onDown(e, e.currentTarget)} onPointerUp={onUp}
                onPointerLeave={() => { if (!drag) { setTip(null); setActive(null); } }} onDoubleClick={resetZoom} />
              {rowVisible.length === 0 && <div className="absolute inset-0 grid place-items-center text-micro text-ink-muted">All series hidden</div>}
              {rowVisible.length > 0 && !rowHasData && rowEmptyNote && (
                <div className="absolute inset-0 grid place-items-center px-6 text-center pointer-events-none" data-testid={`mh-row-empty-${def.id}`}>
                  <span className="text-micro text-ink-muted max-w-sm bg-surface-0/85 px-2 py-1 rounded">{rowEmptyNote}</span>
                </div>
              )}
            </div>
          </div>
        );
      })}
      <div className="px-4 py-2 text-micro text-ink-muted bg-surface-1/60 border-t border-edge-subtle">
        Solid = observed · Dashed = estimated · Break = no observation · Drag to zoom, double-click to reset{src.demo && " · demo fallback data"}
      </div>
    </div>
  );

  const body = (
    <section ref={rootRef} data-testid="market-history" aria-label="Synchronized market history"
      className={cn("bg-surface-0 border border-edge-subtle rounded-panel overflow-hidden", expanded && "h-full flex flex-col border-0 rounded-2xl shadow-none")}>
      <header className={cn("flex flex-wrap items-center gap-3 px-4 py-3 border-b border-edge-subtle bg-surface-0 shrink-0", expanded && "sticky top-0 z-10")}>
        <div className="mr-auto min-w-0">
          <h2 className="text-stitle text-ink-primary">Market History</h2>
          <span className="text-micro text-ink-muted">Observed &amp; estimated signals{src.demo && " · demo data"} · Updated {now.toLocaleString("en-US", { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" })}</span>
        </div>
        {!isSingleObservation && N >= 2 && (
        <div className="flex flex-wrap items-center gap-0.5 min-w-0" role="group" aria-label="Date range">
          {RANGES.map((r) => {
            const enabled = rangeEnabled[r] !== false;
            return (
              <button
                key={r}
                data-testid={`mh-range-${r.toLowerCase()}`}
                onClick={() => applyRange(r)}
                disabled={!enabled}
                title={enabled ? undefined : "Not enough observations for this range"}
                className={cn(
                  "h-8 px-2 rounded-ctrl text-micro font-medium",
                  !enabled && "opacity-40 cursor-not-allowed",
                  enabled && (range === r ? "ez-seg-active" : "ez-seg text-ink-secondary"),
                )}
              >
                {r}
              </button>
            );
          })}
          <DateRangePopover
            allStart={records[0]?.timestamp}
            maxDate={records[N - 1]?.timestamp}
            value={customLabel ? { label: customLabel } : null}
            align="end"
            onApply={applyCustom}
            trigger={
              <button data-testid="mh-range-custom"
                className={cn("h-8 px-2 rounded-ctrl text-micro font-medium inline-flex items-center gap-1", range === "Custom" ? "ez-seg-active" : "ez-seg text-ink-secondary")}>
                {range === "Custom" && customLabel ? customLabel : "Custom"}
              </button>
            }
          />
        </div>
        )}
        <div className="flex flex-wrap items-center gap-0.5 pl-2 border-l border-edge-subtle min-w-0">
          <button data-testid="mh-table" onClick={() => setTable((t) => !t)} className={toolBtn} aria-pressed={table}>{table ? <LineIcon className="h-4 w-4" /> : <TableIcon className="h-4 w-4" />}{table ? "Charts" : "Table"}</button>
          <button data-testid="mh-reset" onClick={resetZoom} className={toolBtn} aria-label="Reset zoom"><RotateCcw className="h-4 w-4" />Reset</button>
          <button data-testid="mh-export" onClick={exportCsv} className={toolBtn} aria-label="Export CSV"><Download className="h-4 w-4" />Export</button>
          <button ref={expandBtnRef} data-testid="mh-expand" onClick={() => setExpanded((x) => !x)} className={toolBtn} aria-expanded={expanded} aria-label={expanded ? "Collapse Market History" : "Expand Market History"}>{expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}<span className="hidden sm:inline">{expanded ? "Collapse" : "Expand"}</span></button>
          {expanded && <button data-testid="mh-close" autoFocus onClick={() => { setExpanded(false); expandBtnRef.current?.focus(); }} className="inline-flex items-center gap-1.5 h-8 px-2.5 rounded-ctrl text-compact text-red-600 hover:bg-red-100" aria-label="Close expanded Market History"><X className="h-4 w-4" />Close</button>}
        </div>
      </header>
      {!loading && !error && !src.unavailable && !src.emptyObserved && src.stats && !table && !isSingleObservation && (
        <HistoryStatsStrip stats={src.stats} availability={src.seriesAvailability} />
      )}
      <div className={cn(expanded && "flex-1 min-h-0 overflow-y-auto ez-scroll")}>{content}</div>
    </section>
  );

  const tipFlipLeft = tip && tip.x > window.innerWidth - 300;
  const tooltip = tip && !table && !loading && !empty && createPortal(
    <div className="fixed z-[140] w-[260px] max-h-[70vh] overflow-auto ez-scroll bg-surface-0 border border-edge-subtle rounded-ctrl shadow-menu p-3 text-micro pointer-events-none" data-testid="mh-tooltip"
      style={{ left: tipFlipLeft ? Math.max(8, tip.x - 276) : Math.min(window.innerWidth - 268, tip.x + 16), top: Math.max(8, Math.min(window.innerHeight - 260, tip.y - 40)) }}>
      <div className="font-semibold text-ink-primary pb-1.5 mb-1.5 border-b border-edge-subtle">{fmtTs(tip.rec.timestamp)}</div>
      {tip.rec.dataQuality === "missing" ? (
        <div className="space-y-1"><div className="text-ink-secondary">No observation available for this period.</div>{tip.rec.gapReason && <div className="text-ink-muted">{tip.rec.gapReason}</div>}</div>
      ) : (
        <div className="space-y-2">
          {TIP_GROUPS.map((g) => (
            <div key={g.title}>
              <div className="text-[10px] uppercase tracking-wide text-ink-muted mb-0.5">{g.title}</div>
              {g.items.map(([k, l, f]) => (
                <div key={k} className="flex items-center justify-between py-0.5">
                  <span className="flex items-center gap-1.5 min-w-0 truncate"><span className="h-2 w-2 rounded-full shrink-0" style={{ background: C[k] }} />{l}</span>
                  <strong className="tabnum text-ink-primary shrink-0">{tip.rec[k] == null ? "No obs." : f(tip.rec[k])}</strong>
                </div>
              ))}
            </div>
          ))}
          <div className="flex items-center justify-between py-0.5 border-t border-edge-subtle pt-1.5"><span className="flex items-center gap-1.5"><span className="h-2 w-2 rounded-full" style={{ background: C.walmartRetail }} />Walmart in stock</span><strong className="text-ink-primary">{tip.rec.walmartRetailInStock ? "Yes" : "No"}</strong></div>
          <div className="text-ink-muted capitalize">Data quality: {tip.rec.dataQuality}</div>
        </div>
      )}
    </div>, document.body);

  return (
    <>
      {expanded
        ? createPortal(
            <div className="fixed inset-0 z-[120] bg-black/45 grid place-items-center p-3 sm:p-6 ez-fade-in" onClick={() => { setExpanded(false); expandBtnRef.current?.focus(); }}>
              <div role="dialog" aria-modal="true" aria-label="Market History (expanded)" onClick={(e) => e.stopPropagation()}
                className="w-[min(94vw,1500px)] h-[min(88vh,920px)] flex flex-col bg-surface-0 rounded-2xl border border-edge-subtle shadow-menu overflow-hidden ez-rise-in">
                {body}
              </div>
            </div>, document.body)
        : body}
      {tooltip}
    </>
  );
}

export default MarketHistory;
